import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Download, RefreshCw } from "lucide-react";
import { fmtINR } from "@/lib/format";

const fyStart = () => {
  const d = new Date();
  return new Date(d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear(), 3, 1).toISOString().slice(0, 10);
};
const fyEnd = () => {
  const d = new Date();
  return new Date(d.getMonth() < 3 ? d.getFullYear() : d.getFullYear() + 1, 2, 31).toISOString().slice(0, 10);
};

/**
 * Period-wise TDS report — one component reused for `kind = "payable" | "receivable"`.
 * Shows: totals row · section-wise breakdown w/ opening / accrued / settled / closing ·
 * month × section heatmap · drill-through detail table.
 */
export default function TDSReport({ kind }) {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(fyEnd());
  const [section, setSection] = useState("all");
  const [sections, setSections] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tds/sections").then((r) => setSections(r.data || []));
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/tds/reports/${kind}`, {
        params: { from_date: from, to_date: to, section: section === "all" ? undefined : section },
      });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [kind]);

  const isPayable = kind === "payable";
  const totals = data?.totals || {};
  const accrueLabel = isPayable ? "Deducted (Cr)" : "TDS accrued (Dr)";
  const settleLabel = isPayable ? "Deposited via challan (Dr)" : "Credit claimed (Cr)";

  const exportCsv = () => {
    if (!data) return;
    const lines = ["Section,Account,Opening,Accrued,Settled,Closing"];
    for (const s of data.by_section || []) {
      lines.push([s.section, s.account_name, s.opening, s.period.accrued, s.period.settled, s.closing].join(","));
    }
    lines.push([`TOTAL`, ``, totals.opening, totals.accrued, totals.settled, totals.closing].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `tds-${kind}-${from}-to-${to}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid={`tds-report-${kind}`}>
      <div className="card-sharp p-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm mt-1 h-8" data-testid={`${kind}-from`} /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm mt-1 h-8" data-testid={`${kind}-to`} /></div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Section</Label>
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="rounded-sm mt-1 h-8 text-xs" data-testid={`${kind}-section`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sections</SelectItem>
              {sections.map((s) => (
                <SelectItem key={s.section} value={s.section}>
                  Sec {s.new_section} · Code {s.section} · {s.description.slice(0, 40)}{s.old_section ? ` (old ${s.old_section})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button onClick={run} disabled={busy} className="rounded-sm bg-accent text-white gap-1.5" data-testid={`${kind}-run`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Run</>}
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={!data} className="rounded-sm gap-1.5" data-testid={`${kind}-export`}>
            <Download className="w-4 h-4" /> CSV
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <KPI label="Opening" value={totals.opening} tone="muted" />
            <KPI label={accrueLabel} value={totals.accrued} tone={isPayable ? "warning" : "info"} />
            <KPI label={settleLabel} value={totals.settled} tone="success" />
            <KPI label="Closing" value={totals.closing} tone={isPayable ? "danger" : "info"} />
          </div>

          <div className="card-sharp overflow-hidden">
            <div className="px-4 py-3 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Section-wise breakdown
            </div>
            <table className="w-full dense">
              <thead>
                <tr>
                  <th>Section</th>
                  <th>Ledger</th>
                  <th className="text-right">Opening</th>
                  <th className="text-right">{accrueLabel}</th>
                  <th className="text-right">{settleLabel}</th>
                  <th className="text-right">Closing</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_section || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted-foreground py-8">
                    {data.note || "No TDS activity in this period."}
                  </td></tr>
                )}
                {(data.by_section || []).map((s) => (
                  <tr key={s.section} data-testid={`section-row-${s.section}`}>
                    <td className="font-mono text-xs">{s.section}</td>
                    <td className="text-xs text-muted-foreground">{s.account_name}</td>
                    <td className="text-right tabular-nums">{fmtINR(s.opening)}</td>
                    <td className="text-right tabular-nums">{fmtINR(s.period.accrued)}</td>
                    <td className="text-right tabular-nums">{fmtINR(s.period.settled)}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(s.closing)}</td>
                  </tr>
                ))}
                {(data.by_section || []).length > 0 && (
                  <tr className="border-t-2 font-medium bg-muted/20">
                    <td colSpan={2}>TOTAL</td>
                    <td className="text-right tabular-nums">{fmtINR(totals.opening)}</td>
                    <td className="text-right tabular-nums">{fmtINR(totals.accrued)}</td>
                    <td className="text-right tabular-nums">{fmtINR(totals.settled)}</td>
                    <td className="text-right tabular-nums">{fmtINR(totals.closing)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {(data.by_month || []).length > 0 && (
            <div className="card-sharp overflow-hidden">
              <div className="px-4 py-3 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Month-wise flow</div>
              <table className="w-full dense">
                <thead><tr><th>Month</th><th className="text-right">{accrueLabel}</th><th className="text-right">{settleLabel}</th></tr></thead>
                <tbody>
                  {data.by_month.map((m) => (
                    <tr key={m.month}>
                      <td className="font-mono text-xs">{m.month}</td>
                      <td className="text-right tabular-nums">{fmtINR(m.accrued)}</td>
                      <td className="text-right tabular-nums">{fmtINR(m.settled)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, value, tone = "muted" }) {
  const toneMap = { muted: "text-slate-700", info: "text-blue-600", success: "text-emerald-600",
                     warning: "text-amber-600", danger: "text-red-600" };
  return (
    <div className="card-sharp p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-2xl font-medium mt-1 tabular-nums ${toneMap[tone]}`}>{fmtINR(value || 0)}</div>
    </div>
  );
}
